import { ReviewResponse } from '../types';

const statusRank = (status: unknown): number => {
  if (status === 'completed') return 3;
  if (status === 'in_progress') return 2;
  if (status === 'not_started') return 1;
  return 0;
};

const timestampMs = (value: unknown): number => {
  if (!value) return 0;
  const time = new Date(value as any).getTime();
  return Number.isFinite(time) ? time : 0;
};

const hasMeaningfulResponseData = (responseData: unknown): boolean => {
  if (!responseData || typeof responseData !== 'object' || Array.isArray(responseData)) {
    return false;
  }

  return Object.values(responseData).some(value => {
    if (value == null) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.keys(value).length > 0;
    return true;
  });
};

const contentRank = (response: Partial<ReviewResponse>): number => {
  let rank = 0;
  if (response.isPresent !== undefined && response.isPresent !== null) rank += 1;
  if (response.roomId !== undefined && response.roomId !== null) rank += 1;
  if (response.floor) rank += 1;
  if (response.notes && response.notes.trim().length > 0) rank += 2;
  if (hasMeaningfulResponseData(response.responseData)) rank += 3;
  if (response.photos && response.photos.length > 0) rank += 3;
  return rank;
};

export function shouldPreferResponse<T extends Partial<ReviewResponse>>(
  candidate: T,
  current: T
): boolean {
  const candidateStatusRank = statusRank(candidate.status);
  const currentStatusRank = statusRank(current.status);
  if (candidateStatusRank !== currentStatusRank) {
    return candidateStatusRank > currentStatusRank;
  }

  const candidateContentRank = contentRank(candidate);
  const currentContentRank = contentRank(current);
  if (candidateContentRank !== currentContentRank) {
    return candidateContentRank > currentContentRank;
  }

  const candidateTime = timestampMs(candidate.updatedAt || candidate.createdAt);
  const currentTime = timestampMs(current.updatedAt || current.createdAt);
  if (candidateTime !== currentTime) {
    return candidateTime > currentTime;
  }

  return Number(candidate.id || 0) > Number(current.id || 0);
}

export function selectBestResponseForItem<T extends Partial<ReviewResponse>>(
  responses: T[] | undefined | null,
  itemId: number
): T | undefined {
  if (!responses) {
    return undefined;
  }

  return responses
    .filter(response => Number(response.itemId) === Number(itemId))
    .reduce<T | undefined>((best, response) => {
      if (!best || shouldPreferResponse(response, best)) {
        return response;
      }

      return best;
    }, undefined);
}

export function dedupeResponsesByItemId<T extends Partial<ReviewResponse>>(
  responses: T[] | undefined | null
): T[] {
  if (!responses || responses.length === 0) {
    return [];
  }

  const byItemId = new Map<number, T>();
  const withoutItemId: T[] = [];

  for (const response of responses) {
    if (response.itemId == null) {
      withoutItemId.push(response);
      continue;
    }

    const itemId = Number(response.itemId);
    const existing = byItemId.get(itemId);
    if (!existing || shouldPreferResponse(response, existing)) {
      byItemId.set(itemId, response);
    }
  }

  return [...withoutItemId, ...byItemId.values()];
}

export function normalizeReviewResponses<T extends { responses?: Partial<ReviewResponse>[] | null }>(
  review: T
): T {
  if (!review.responses) {
    return review;
  }

  return {
    ...review,
    responses: dedupeResponsesByItemId(review.responses),
  } as T;
}
